import { lowerFirst } from 'lodash';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, Link, MenuItem, Tooltip } from '@mui/joy';
import { Link as RRLink } from 'react-router-dom';

import { layout } from '@/shared/utils/constants';
import { type TopDueTask } from '@/types/tasks';
import useDayjs from '@/shared/hooks/useDayjs';
import { useTaskActions } from '@/mutations';

import { PriorityIcon, SnoozeIcon, TaskIcon, WarningIcon } from '@/shared/icons/Icons';

type Props = {
  task: TopDueTask;
};

const Content = ({ task }: { task: TopDueTask }) => {
  const { t, i18n } = useTranslation();

  const { formatDate, isFutureDate } = useDayjs();
  const shouldShowTaskDescription = task.description !== (task.systemTaskType || task.teamTaskType);
  const systemTaskType = task.systemTaskType ?? '';
  return (
    <div className="text-xs">
      <p>
        <span className="mr-2">
          <PriorityIcon priority={task.priority} />
        </span>
        {task.linkDescription == null || !task.entityType ? (
          task.description
        ) : (
          <span className="text-link">{task.linkDescription}</span>
        )}
      </p>
      <p>
        {!isFutureDate(task.dueDate) && (
          <Tooltip title={t('dueDateHasBeenPassed')} sx={{ zIndex: layout.layers.TOOLTIP }}>
            <i className="text-orange-500">
              <WarningIcon /> {formatDate(task.dueDate)}
            </i>
          </Tooltip>
        )}
      </p>
      <p className="line-clamp-5">
        <span className="font-semibold">
          {i18n.exists(lowerFirst(systemTaskType))
            ? t(lowerFirst(systemTaskType))
            : task.teamTaskType}
          {shouldShowTaskDescription && ': '}
        </span>
        {shouldShowTaskDescription && task.description}
      </p>
    </div>
  );
};

const TaskMenuItem: React.FC<Props> = ({ task }) => {
  const { finishTask, snoozeTask, isFinishingTask, isSnoozingTask } = useTaskActions();

  return (
    <MenuItem className="flex py-2 text-xs">
      <Box className="grow">
        {task.entityType ? (
          <Link
            component={RRLink}
            overlay
            to={`/${task.entityType.toLowerCase()}/${task.entityId}`}
            underline="none"
            onClick={(e) => e.stopPropagation()}
          >
            <Content task={task} />
          </Link>
        ) : (
          <Content task={task} />
        )}
      </Box>

      <Box className="flex items-center">
        <IconButton
          disabled={isFinishingTask}
          loading={isSnoozingTask}
          variant="plain"
          onClick={() => snoozeTask(task.id)}
        >
          <SnoozeIcon />
        </IconButton>
        <IconButton
          disabled={isSnoozingTask}
          loading={isFinishingTask}
          variant="plain"
          onClick={() => finishTask(task.id)}
        >
          <TaskIcon />
        </IconButton>
      </Box>
    </MenuItem>
  );
};

export default TaskMenuItem;
